import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  Input,
  OnDestroy,
  QueryList,
  ViewEncapsulation,
  inject,
  output
} from '@angular/core';
import {
  AccordionModule,
  AccordionTabCloseEvent,
  AccordionTabOpenEvent
} from 'primeng/accordion';
import { Subscription } from 'rxjs';
import { MAccordionPanelComponent } from '../m-accordion-panel/m-accordion-panel.component';

export type MAccordionValue =
  | string
  | number
  | (string | number)[]
  | undefined
  | null;

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'm-accordion',
  templateUrl: './m-accordion.component.html',
  styleUrls: ['./m-accordion.component.scss'],
  imports: [NgClass, NgTemplateOutlet, AccordionModule],
  encapsulation: ViewEncapsulation.None
})
export class MAccordionComponent implements AfterContentInit, OnDestroy {
  @Input() value: MAccordionValue = undefined;
  @Input() multiple = false;
  @Input() styleClass = '';

  readonly valueChange = output<MAccordionValue>();
  // PrimeNG-compatible names — keep so consumers migrating from p-accordion can reuse (onOpen)/(onClose) bindings unchanged.
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  readonly onOpen = output<AccordionTabOpenEvent>();
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  readonly onClose = output<AccordionTabCloseEvent>();

  @ContentChildren(MAccordionPanelComponent)
  private panelQuery!: QueryList<MAccordionPanelComponent>;

  panels: MAccordionPanelComponent[] = [];

  private cdr = inject(ChangeDetectorRef);
  private panelSub?: Subscription;
  // Subscriptions to each panel's inputChange; rebuilt whenever the panel set
  // changes so a runtime change to any panel input re-checks this OnPush view.
  private panelInputSub = new Subscription();

  ngAfterContentInit(): void {
    this.refreshPanels();
    this.subscribeToPanelInputs();
    this.panelSub = this.panelQuery.changes.subscribe(() => {
      this.refreshPanels();
      this.subscribeToPanelInputs();
      this.cdr.markForCheck();
    });

    // Open panels flagged [active]=true. Setting `value` here is NG0100-safe
    // and opens them on first paint: our own template binding ([value]="value")
    // is first checked after this hook runs. Only the valueChange emit must be
    // deferred — emitting synchronously would mutate the host's already-checked
    // binding within the same change-detection cycle (NG0100).
    const initiallyActive = this.panels.filter((p) => p.active);
    if (initiallyActive.length === 0) {
      return;
    }

    let changed = false;
    if (this.multiple) {
      const merged = Array.isArray(this.value) ? [...this.value] : [];
      for (const panel of initiallyActive) {
        if (!merged.includes(panel.value)) {
          merged.push(panel.value);
          changed = true;
        }
      }
      if (changed) {
        this.value = merged;
      }
    } else {
      // Single mode: last active panel wins, matching PrimeNG's single-open model.
      const next = initiallyActive[initiallyActive.length - 1]?.value;
      changed = this.value !== next;
      if (changed) {
        this.value = next;
      }
    }

    if (changed) {
      queueMicrotask(() => this.valueChange.emit(this.value));
    }
  }

  ngOnDestroy(): void {
    this.panelSub?.unsubscribe();
    this.panelInputSub.unsubscribe();
  }

  handleValueChange(next: MAccordionValue): void {
    this.value = next;
    this.valueChange.emit(next);
  }

  setActiveValue(panelValue: string | number): void {
    if (this.multiple) {
      const current = Array.isArray(this.value) ? [...this.value] : [];
      if (!current.includes(panelValue)) {
        current.push(panelValue);
        this.handleValueChange(current);
      }
    } else if (this.value !== panelValue) {
      this.handleValueChange(panelValue);
    }
  }

  private refreshPanels(): void {
    this.panels = this.panelQuery.toArray();
  }

  private subscribeToPanelInputs(): void {
    this.panelInputSub.unsubscribe();
    this.panelInputSub = new Subscription();
    for (const panel of this.panels) {
      this.panelInputSub.add(
        panel.inputChange.subscribe(() => this.cdr.markForCheck())
      );
    }
  }
}
